import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import iban from 'iban';

import suitcss from '../../../helpers/suitcss';
import connectUI from '../../basics/ui/UIConnector';
import FieldInput from '../../basics/form/FieldInput';
import FieldCheckbox from '../../basics/form/FieldCheckbox';
import FieldSelectbox from '../../basics/form/FieldSelectbox';
import Copy from '../../basics/text/TextCopy';
import CreditCardWrapper from '../../basics/form/CreditCardWrapper';
import CreditCardInfo from '../../basics/form/CreditCardInfo';
import {
  FORM_FIELD_PAYMENT_TYPE_ADVANCE,
  FORM_FIELD_PAYMENT_TYPE_CC,
  FORM_FIELD_PAYMENT_TYPE_DEBIT,
  FORM_FIELD_PAYMENT_TYPE_PAYPAL,
} from '../../../helpers/constants';

class PaymentModule extends PureComponent {

  constructor(props, context) {
    super(props, context);
    this.onIbanInput = this.onIbanInput.bind(this);
    this.ibanLength = 3;
    this.state = {
      errors: 0,
    };
  }

  onError = (err) => {
    this.props.onError(err);
    this.setState({
      errors: ++this.state.errors,
    });
  };

  onCreditCardDataAvailable = (data) => {
    this.props.onCreditCardDataAvailable(data);
    this.props.onError(false);
  };

  onIbanInput(ev) {
    if (ev.target) {

      const index = ev.target.selectionStart;
      ev.target.value = iban.printFormat(ev.target.value);  // eslint-disable-line

      if (index % 5 === 0) {

        if (this.ibanLength > index) {
          ev.target.setSelectionRange(index - 1, index - 1);
        } else {
          ev.target.setSelectionRange(index + 1, index + 1);
          this.ibanLength++;
        }

      } else {
        ev.target.setSelectionRange(index, index);
      }

      this.ibanLength++;
      this.ibanLength = index;
    }
  }

  renderDirectDebitOwner() {
    const {
      personalData, formValues, fieldMap, form,
    } = this.props;
    // debit owner in my data (myDataPaymentForm) should be the customer data
    // debit owner in activate and order process should be the same as in the personal data step
    const hasFormValueName = formValues && fieldMap.firstName && fieldMap.lastName;
    const directDebitOwner = form !== 'myDataPaymentForm' && hasFormValueName ?
      `${formValues[fieldMap.firstName.name]} ${formValues[fieldMap.lastName.name]}` :
      `${personalData.firstName} ${personalData.lastName}`;
    return (
      <Copy className={suitcss({ utilities: ['weightBold', 'col12'] })}>
        Name des Kontoinhabers: {directDebitOwner}
      </Copy>
    );
  }

  renderDirectDebit() {
    const { fieldMap } = this.props;
    const hasPaymentOptions = fieldMap.paymentType.options.length > 1;
    return (
      <div className={suitcss({ utilities: ['grid'] })}>
        {!hasPaymentOptions &&
          this.renderDirectDebitOwner()
        }
        <Field {...fieldMap.mediaId} component={FieldInput} utilities={['col12']} withToolTip onInput={this.onIbanInput} />
        {hasPaymentOptions &&
          this.renderDirectDebitOwner()
        }
        <Field {...fieldMap.ddMandate} component={FieldCheckbox} raw utilities={['col12']} />
      </div>
    );
  }

  renderCreditCard() {
    const { creditCardData, ui, personalData } = this.props;
    return (
      <div className={suitcss({ utilities: ['grid'] })}>
        <Copy className={suitcss({ utilities: ['col12'] })} embedded raw>
          {ui.txtGuiPaymentRecipientVisa}
        </Copy>
        <div className={suitcss({ utilities: ['col12'] })} >
          {creditCardData &&
            <div >
              <h3>{ui.guiPaymentCreditCardInfo}</h3>
              <CreditCardInfo data={creditCardData} />
            </div>
          }
          {process.browser && !creditCardData &&
            <CreditCardWrapper
              key={this.state.errors}
              personalData={personalData}
              host={process.Environment.getVariableAsString('DBN_PAYMENT_CREDITCARDHOST')}
              onCreditCardDataAvailable={this.onCreditCardDataAvailable}
              onError={this.onError}
            />
          }
        </div>
      </div>
    );
  }

  renderMethodAdvance() {
    const { ui } = this.props;
    return (
      <div>
        <Copy embedded raw>{ui.guiPaymentAdvanceInfo}</Copy>
      </div>
    );
  }

  renderPaypal() {
    const { ui } = this.props;
    return (
      <div>
        <Copy embedded raw>{ui.paymentPaypalInfo}</Copy>
      </div>
    );
  }

  render() {
    const {
      className,
      paymentType,
      fieldMap,
      resetCreditCardData,
    } = this.props;

    const showPaymentSwitch = fieldMap.paymentType.options.length > 1;

    // feature toggle for paypal; we only display the paypal radiobutton if feature is toggled
    const fieldMapPaymentType = {
      ...fieldMap.paymentType,
      options: process.Environment.getVariableAsBoolean('DBN_FEATURE_PAYPAL')
        ? fieldMap.paymentType.options
        : fieldMap.paymentType.options.filter(
          paymentOption => paymentOption.value !== FORM_FIELD_PAYMENT_TYPE_PAYPAL,
        ),
    };

    const renderContent = {
      [FORM_FIELD_PAYMENT_TYPE_DEBIT]: this.renderDirectDebit,
      [FORM_FIELD_PAYMENT_TYPE_CC]: this.renderCreditCard,
      [FORM_FIELD_PAYMENT_TYPE_ADVANCE]: this.renderMethodAdvance,
      [FORM_FIELD_PAYMENT_TYPE_PAYPAL]: this.renderPaypal,
    };

    return (
      <div className={suitcss({ className, utilities: ['grid'] }, this)}>
        {showPaymentSwitch &&
          <Field {...fieldMapPaymentType} onChange={resetCreditCardData} component={FieldSelectbox} utilities={['col12']} withToolTip />
        }
        {renderContent[paymentType].call(this)}
      </div>
    );
  }
}

PaymentModule.propTypes = {
  className: PropTypes.string,
  onCreditCardDataAvailable: PropTypes.func.isRequired,
  resetCreditCardData: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  paymentType: PropTypes.string.isRequired,
  fieldMap: (props, propName, compName) => {
    const fieldMap = props[propName];
    if (typeof fieldMap !== 'object' || !Object.keys(fieldMap).length) {
      return new Error(`No fields supplied prop '${propName}' supplied to '${compName}'.`);
    }

    const paymentTypeOptions = (fieldMap.paymentType || {}).options;
    if (!Array.isArray(paymentTypeOptions) || !paymentTypeOptions.length) {
      return new Error(`Missing payment options in prop '${propName}' supplied to '${compName}'.`);
    }

    const paymentType = props.paymentType;
    if (!paymentTypeOptions.filter(option => option.value === paymentType).length) {
      return new Error(`Prop '${propName}' supplied to '${compName}' has no option for payment type '${paymentType}.`);
    }

    // all fine
    return null;
  },
  creditCardData: PropTypes.object,
  ui: PropTypes.shape({
    guiPaymentAdvanceInfo: PropTypes.string.isRequired,
    guiPaymentCreditCardInfo: PropTypes.string.isRequired,
  }).isRequired,
  personalData: PropTypes.object,
  formValues: PropTypes.object,
  form: PropTypes.string,
};

export default connectUI()(PaymentModule);
